'use strict';

;( () => {

	document.addEventListener( 'alpine:init', () => {

		Alpine.data( 'dissolveTransition', () => ({
			blockSize: 15,
			speed    : 400,
			scatter  : 400,

			_timers : [],
			_tiles  : [],
			_clearBg: false,

			init() {
				this.buildGrid();
			},

			reveal() {
				if ( window.matchMedia( '( width < 64em )' ).matches ) {
					if ( this.$refs.grid ) this.$refs.grid.remove();
					this.$el.style.background = 'transparent';
					return;
				}

				this._clearTimers();
				this._showAll();

				const tiles              = [...this._tiles];
				const { speed, scatter } = this;

				// Defer animation start to next frame to ensure smooth rendering.
				requestAnimationFrame( () => {

					// Shuffle.
					for ( let i = tiles.length - 1; 0 < i; i-- ) {
						const j = Math.floor( Math.random() * ( i + 1 ) );
						[tiles[i], tiles[j]] = [tiles[j], tiles[i]];
					}

					let latestDelay = 0;
					let lastTile    = null;

					tiles.forEach( ( tile, i ) => {
						const delay = ( i / tiles.length ) * scatter + Math.random() * ( scatter * 0.3 );

						if ( delay > latestDelay ) {
							latestDelay = delay;
							lastTile    = tile;
						}

						const t = setTimeout( () => {
							tile.style.transition = `opacity ${speed}ms cubic-bezier(0.22, 1, 0.36, 1)`;
							tile.style.opacity    = '0';
						}, delay );
						this._timers.push( t );
					});

					if ( lastTile ) {
						lastTile.addEventListener( 'transitionend', () => {
							this.$refs.grid.remove();
							if ( this._clearBg ) {
								this.$el.style.background = 'transparent';
							}
						}, { once: true });
					}
				});
			},

			buildGrid() {
				if ( window.matchMedia( '( width < 64em )' ).matches ) return;

				const grid          = this.$refs.grid;
				const W             = this.$el.offsetWidth;
				const H             = this.$el.offsetHeight;
				const { blockSize } = this;
				const cols          = Math.ceil( W / blockSize );
				const rows          = Math.ceil( H / blockSize );
				const styles        = getComputedStyle( this.$el );
				const bgColor       = styles.backgroundColor;
				const bgImage       = styles.backgroundImage;

				// Check for a referenced background element (e.g. an abs-positioned image or pattern sibling).
				// Scope search to the nearest <section> so multiple instances on a page don't cross-reference.
				const bgElSelector  = this.$el.dataset.bgEl;
				const bgElScope     = this.$el.closest( 'section' ) || document;
				const bgShade       = this.$el.dataset.bgShade || null;
				const bgEl          = bgElSelector ? bgElScope.querySelector( bgElSelector ) : null;
				const bgImgEl       = bgEl
					? ( 'IMG' === bgEl.tagName ? bgEl : bgEl.querySelector( 'img' ) )
					: null;

				const hasElement  = !! bgImgEl;
				const hasBgCanvas = !! bgEl && 'CANVAS' === bgEl.tagName;
				const hasBgElCss  = !! bgEl && ! hasElement && ! hasBgCanvas; // bgEl exists but has no <img> — CSS bg pattern
				const hasCssBg    = ! bgEl && bgImage && 'none' !== bgImage;

				let imgOffsetX, imgOffsetY, imgRect, imgBgSize, imgBgPos;
				let bgElRect, bgElCssStyles, bgElOffsetX, bgElOffsetY, bgElBgImage;

				if ( hasElement ) {
					const containerRect = this.$el.getBoundingClientRect();
					imgRect    = bgImgEl.getBoundingClientRect();
					imgOffsetX = imgRect.left - containerRect.left;
					imgOffsetY = imgRect.top  - containerRect.top;

					const imgStyles = getComputedStyle( bgImgEl );
					const fit       = imgStyles.objectFit;
					imgBgSize = 'fill' === fit ? '100% 100%' : ( fit || 'cover' );
					imgBgPos  = imgStyles.objectPosition || 'center';
				}

				this._clearBg = hasBgElCss || hasBgCanvas;

				if ( hasBgElCss ) {
					const containerRect = this.$el.getBoundingClientRect();
					bgElRect      = bgEl.getBoundingClientRect();
					bgElOffsetX   = bgElRect.left - containerRect.left;
					bgElOffsetY   = bgElRect.top  - containerRect.top;
					bgElCssStyles = getComputedStyle( bgEl );

					// If backgroundImage is still a var() reference (not resolved by the browser),
					// walk the custom property chain to get the actual value.
					bgElBgImage = bgElCssStyles.backgroundImage;
					if ( bgElBgImage && bgElBgImage.trim().startsWith( 'var(' ) ) {
						const varMatch = bgElBgImage.match( /var\(\s*(--[\w-]+)/ );
						if ( varMatch ) {
							const propVal = bgElCssStyles.getPropertyValue( varMatch[1] ).trim();
							if ( propVal ) bgElBgImage = propVal;
						}
					}
				}

				grid.innerHTML = '';
				this._tiles    = [];

				for ( let r = 0; r < rows; r++ ) {

					for ( let c = 0; c < cols; c++ ) {
						const left         = c * blockSize;
						const top          = r * blockSize;
						const tile         = document.createElement( 'div' );
						tile.style.cssText = `
							position   : absolute;
							left       : ${left}px;
							top        : ${top}px;
							width      : ${blockSize}px;
							height     : ${blockSize}px;
							opacity    : 1;
							will-change: opacity;
							${( hasElement || hasCssBg || hasBgElCss ) ? 'overflow: hidden;' : `background: ${bgColor};`}
						`;

						if ( hasCssBg ) {
							const inner         = document.createElement( 'div' );
							inner.style.cssText = `
								position           : absolute;
								left               : ${-left}px;
								top                : ${-top}px;
								width              : ${W}px;
								height             : ${H}px;
								background-color   : ${bgColor};
								background-image   : ${bgImage};
								background-size    : ${styles.backgroundSize};
								background-position: ${styles.backgroundPosition};
								background-repeat  : ${styles.backgroundRepeat};
							`;
							tile.appendChild( inner );

						} else if ( hasElement ) {
							const inner         = document.createElement( 'div' );
							inner.style.cssText = `
								position           : absolute;
								left               : ${imgOffsetX - left}px;
								top                : ${imgOffsetY - top}px;
								width              : ${imgRect.width}px;
								height             : ${imgRect.height}px;
								background-image   : url(${bgImgEl.currentSrc || bgImgEl.src});
								background-size    : ${imgBgSize};
								background-position: ${imgBgPos};
								background-repeat  : no-repeat;
								filter             : blur( var( --blur-xs ) );
							`;

							if ( bgShade ) {
								const lightenedGradient = 'linear-gradient( 270deg, rgba( 23, 23, 23, 0.575 ) 0%, rgba( 23, 23, 23, 0.1 ) 100% )';
								inner.style.backgroundImage = `${lightenedGradient}, ${inner.style.backgroundImage}`;
							}

							tile.appendChild( inner );

						} else if ( hasBgElCss ) {
							// Tile base = solid mask color; inner = the pattern overlay at its natural opacity.
							tile.style.background = bgColor;

							const inner         = document.createElement( 'div' );
							inner.style.cssText = `
								position           : absolute;
								left               : ${bgElOffsetX - left}px;
								top                : ${bgElOffsetY - top}px;
								width              : ${bgElRect.width}px;
								height             : ${bgElRect.height}px;
								background-image   : ${bgElBgImage};
								background-size    : ${bgElCssStyles.backgroundSize};
								background-position: ${bgElCssStyles.backgroundPosition};
								background-repeat  : ${bgElCssStyles.backgroundRepeat};
								opacity            : ${bgElCssStyles.opacity};
							`;
							tile.appendChild( inner );
						}

						grid.appendChild( tile );
						this._tiles.push( tile );
					}
				}
			},

			_showAll() {
				this._tiles.forEach( tile => {
					tile.style.transition = 'none';
					tile.style.opacity    = '1';
				});
			},

			_clearTimers() {
				this._timers.forEach( clearTimeout );
				this._timers = [];
			},
		}) );
	});
})();
